import { isString, isUndefined, mapValues, omitBy } from "lodash";
import { QueryParams, QueryParamValue } from "@/utils/queryParams";

export const REDACTED = "[redacted]";

const secretParamPattern = /token|key|password|secret|ssn|code|^ref$/i;
const staticSegmentPattern = /^[a-zA-Z-]*\d?$/;
const absoluteUrlPattern = /https?:\/\/[^\s"'()<>[\]]+/g;
const scriptPathPattern = /\.(m?js|tsx?|css)$/;
const stackPositionPattern = /(:\d+){1,2}$/;

export type Params = Record<string, string | undefined>;

export function isSecretParam(name: string): boolean {
  return secretParamPattern.test(name);
}

export function filterParams<V>(params: Record<string, V | undefined>): Record<string, V | string> {
  return Object.fromEntries(
    Object.entries(params)
      .filter((entry): entry is [string, V] => entry[1] !== undefined)
      .map(([name, value]) => [name, isSecretParam(name) ? REDACTED : value]),
  );
}

export function filterQueryParams(params: QueryParams): QueryParams {
  return mapValues(omitBy(params, isUndefined), (value, name) =>
    isSecretParam(name) ? REDACTED : filterQueryValue(value),
  );
}

function filterQueryValue(value: QueryParamValue): QueryParamValue {
  if (Array.isArray(value)) return value.map(filterQueryValue);
  return isString(value) || isUndefined(value) ? value : filterQueryParams(value);
}

export function filterUrl(url: string): string {
  const parsed = parseUrl(url);
  if (!parsed) {
    return REDACTED;
  }
  const path = parsed.pathname
    .split("/")
    .map((segment) => (staticSegmentPattern.test(segment) ? segment : "*"))
    .join("/");
  const query = new URLSearchParams(
    filterParams(Object.fromEntries(parsed.searchParams)),
  ).toString();
  return query ? `${path}?${query}` : path;
}

export function filterText(text: string): string {
  return text.replace(absoluteUrlPattern, filterUrlInText);
}

function filterUrlInText(match: string): string {
  const url = match.replace(stackPositionPattern, "");
  const parsed = parseUrl(url);
  return parsed && scriptPathPattern.test(parsed.pathname) ? match : filterUrl(url);
}

function parseUrl(url: string): URL | undefined {
  try {
    return new URL(url, "http://localhost");
  } catch {
    return undefined;
  }
}
